import { useQuery } from "@apollo/client";
import React, { useEffect, useRef, useState } from "react";

import { Sentry } from "@compono/sentry-browser";
import {
  Box,
  Button,
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogHeading,
  DialogPrimaryActions,
  Text,
  createContext,
} from "@compono/ui";

// import * as analytics from "../../../shortlyster-ui/src/lib/auth/analytics";
import { SectionLoader } from "~/components/SectionLoader";
import { useAuthentication } from "~/hooks/useAuthentication";
import { useApolloClients } from "~/hooks/useApolloClients";

import { resolveDevelopPermissions, resolvePermissions } from "./permissions";
import type { Permissions } from "./permissions";
import {
  FetchEmployerProfile,
  FetchEngagePermissions,
  FetchEmployerPermissions,
  EmployerPermissions,
  EngagePermissions as NativeEngagePermissions,
  FetchTrackyrPermissions,
  TrackyrPermissions,
} from "./queries";
import {
  Employer,
  FetchEmployerProfileData,
  Organisation,
  User,
} from "./types";
import {
  useFetchDevelopPermissionsQuery,
  useMyGroupMembersQuery,
} from "~/lib/federationGateway/graphql";

export type AuthorizationContext = {
  user: User;
  userId: string;
  orgId: string;
  organisation: Organisation;
  permissions: Permissions;
};

const [useAuthorization, AuthorizationContext] =
  createContext<AuthorizationContext>({
    name: "Authorization",
  });

const AuthorizationConsumer = AuthorizationContext.Consumer;

export type AuthorizationProviderProps = {
  children: React.ReactNode;
};

const AuthorizationProvider = ({ children }: AuthorizationProviderProps) => {
  const {
    loginWithRedirect,
    logout,
    isAuthenticated,
    isAutoSigningIn,
    orgId,
    removeUser,
    events,
  } = useAuthentication();
  const {
    employerClient,
    engageClient,
    shortlysterClient,
    trackyrClient,
    federationGatewayClient,
  } = useApolloClients();
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>();

  // TODO @jg useEffect(() => analytics.terminate, []);

  // try to re-authenticate the user
  const triggerReauthenticate = () =>
    loginWithRedirect({
      state: {
        returnTo: `${window.location?.pathname}${window.location?.search}`,
      },
    });

  // logout user
  const triggerLogout = () =>
    logout({
      state: {
        returnTo: `${window.location?.pathname}${window.location?.search}`,
      },
    });

  const onError = (e) => {
    Sentry.flush(3000).then(() => {
      console.error("AuthorizationProvider error, triggering logout", e);
      triggerReauthenticate();
    });
  };

  // skip queries if we are auto signing in or if there is no orgId
  const shouldSkip = isAutoSigningIn || !orgId;

  // if orgId is false, it means user does not belong to any orgs, so log them out
  useEffect(() => {
    if (isAuthenticated && orgId === false) {
      console.warn("User does not belong to any organisations, logging out");
      triggerLogout();
    }
  }, [orgId, isAuthenticated]);

  useEffect(() => {
    return events.addAccessTokenExpired(() => {
      setIsSessionExpired(true);
    });
  }, [events]);

  const handleSessionExpiredAcknowledgement = () => {
    removeUser();
    triggerReauthenticate();
  };

  const { data, loading } = useQuery<FetchEmployerProfileData>(
    FetchEmployerProfile,
    {
      variables: { organisationId: orgId },
      client: shortlysterClient,
      onError,
      skip: shouldSkip,
    }
  );

  const { data: tpData, loading: tpLoading } = useQuery<{
    permissions: TrackyrPermissions;
  }>(FetchTrackyrPermissions, {
    variables: { organisationId: orgId },
    client: trackyrClient,
    onError,
    skip: shouldSkip,
  });

  const { data: epData, loading: epLoading } = useQuery<{
    permissions: EmployerPermissions;
    recruiterProfile: Employer;
  }>(FetchEmployerPermissions, {
    variables: { organisationId: orgId },
    client: employerClient,
    onError,
    skip: shouldSkip,
  });

  const { data: developData, loading: developLoading } =
    useFetchDevelopPermissionsQuery({
      client: federationGatewayClient,
      skip: shouldSkip,
    });

  const { data: groupsData, loading: groupsLoading } = useMyGroupMembersQuery({
    client: federationGatewayClient,
    skip: shouldSkip,
  });

  const {
    data: enpData,
    loading: enpLoading,
    error: enpError, // Cannot read properties of undefined (reading 'code') for user's without access to Engage
  } = useQuery<{
    permissions: NativeEngagePermissions;
  }>(FetchEngagePermissions, {
    variables: { orgId },
    client: engageClient,
    context: { headers: { orgId } },
    skip: shouldSkip,
    // onError, error is "normal" when user does not have engage perms
  });

  if (
    isAutoSigningIn ||
    loading ||
    tpLoading ||
    enpLoading ||
    epLoading ||
    developLoading ||
    groupsLoading ||
    !data ||
    !tpData ||
    (!enpData && !enpError) ||
    !epData
    // ||!developData
  ) {
    return (
      <Box sx={{ height: "100vh" }}>
        <SectionLoader />
      </Box>
    );
  }

  const { currentOrganisation } = data.recruiterProfile;

  // get all product codes for the current organisation
  const allProductCodes =
    epData.recruiterProfile.currentOrganisation.products?.map(
      (p) => p.productCode
    ) || [];
  // use roles from employer gateway as it returns all roles instead of just the ones for the current product
  const allRoles = epData.recruiterProfile.accesses
    ?.find((a) => a.organisationId === orgId)
    ?.allAppsRoles?.filter((r) => allProductCodes.includes(r.appCode));

  const developPermissions = resolveDevelopPermissions(developData, groupsData);
  const permissions = {
    ...resolvePermissions(
      allRoles,
      data.permissions,
      tpData.permissions,
      epData?.permissions,
      enpData?.permissions
    ),
    develop: developPermissions,
  };

  return (
    <AuthorizationContext.Provider
      value={{
        user: {
          ...data.profile,
          ...data.recruiterProfile,
          // overwrites with data from employer gateway
          // this is due to the fact that other gateways filter accesses by product
          accesses: epData.recruiterProfile.accesses,
        },
        userId: data.profile.id,
        orgId: currentOrganisation?.id || "",
        organisation: currentOrganisation,
        permissions,
      }}
    >
      {children}
      <SessionExpiredDialog
        isOpen={isSessionExpired}
        onClose={handleSessionExpiredAcknowledgement}
      />
    </AuthorizationContext.Provider>
  );
};

const SessionExpiredDialog = (props: {
  isOpen?: boolean;
  onClose?: () => void;
}) => {
  const dialogRef = useRef(null);
  const { isOpen = false, onClose } = props;
  return (
    <Dialog
      ref={dialogRef}
      isOpen={isOpen}
      size="sm"
      onDismiss={onClose}
      aria-describedby="dialog-description-session-expired"
      aria-labelledby="dialog-title-session-expired"
    >
      <DialogHeading id="dialog-title-session-expired">
        You have been signed out
      </DialogHeading>
      <DialogBody id="dialog-description-session-expired">
        <Text>
          Looks like you've been away for a bit. We've signed you out to protect
          your account.
        </Text>
      </DialogBody>
      <DialogPrimaryActions>
        <Button onClick={onClose}>Sign in again</Button>
      </DialogPrimaryActions>
      <DialogCloseButton onClick={onClose} />
    </Dialog>
  );
};

export {
  useAuthorization,
  AuthorizationProvider,
  AuthorizationConsumer as AuthConsumer,
  AuthorizationContext,
};
